import { ArrowForwardRounded, CheckCircleRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PRICING_AUDIENCES } from "../content";
import { PageContainer, SectionHeading, sectionSx } from "./SectionLayout";

function PricingAudienceToggle({ activeAudience, onChange }) {
  return (
    <Box
      aria-label="Choose pricing audience"
      role="group"
      sx={{
        bgcolor: "landing.surfaceContainer",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 999,
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        mb: { xs: 4, md: 6 },
        mx: "auto",
        p: 0.75,
        width: { xs: "100%", sm: 360 }
      }}
    >
      {PRICING_AUDIENCES.map((audience) => {
        const isActive = audience.id === activeAudience;

        return (
          <ButtonBase
            key={audience.id}
            aria-pressed={isActive}
            onClick={() => onChange(audience.id)}
            sx={{
              bgcolor: isActive ? "primary.main" : "transparent",
              borderRadius: 999,
              color: isActive ? "common.white" : "text.secondary",
              fontSize: { xs: 12, sm: 13 },
              fontWeight: 600,
              minHeight: 44,
              px: { xs: 1, sm: 2 },
              transition: "background-color 180ms ease, color 180ms ease",
              "&:hover": {
                bgcolor: isActive ? "primary.dark" : alpha("#451ebb", 0.07),
                color: isActive ? "common.white" : "primary.main"
              }
            }}
          >
            {audience.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

function formatPrice(plan) {
  if (plan.price === 0) return "Free";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: plan.currency,
    maximumFractionDigits: 0
  }).format(plan.price);
}

function PricingCard({ plan, onSelect }) {
  const isFeatured = plan.isPopular;
  const priceLabel = formatPrice(plan);

  return (
    <Card
      elevation={0}
      sx={{
        background: isFeatured
          ? "linear-gradient(155deg, #451ebb 0%, #6848de 100%)"
          : "background.paper",
        border: "1px solid",
        borderColor: isFeatured ? "primary.main" : "divider",
        borderRadius: 3,
        boxShadow: isFeatured
          ? "0 24px 55px rgba(69, 30, 187, 0.22)"
          : "0 14px 38px rgba(55, 36, 111, 0.07)",
        color: isFeatured ? "common.white" : "text.primary",
        display: "flex",
        flexDirection: "column",
        height: { xs: 560, sm: 580, md: 600, lg: 560 },
        overflow: "visible",
        position: "relative",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          boxShadow: isFeatured
            ? "0 28px 62px rgba(69, 30, 187, 0.28)"
            : "0 20px 46px rgba(55, 36, 111, 0.12)",
          transform: "translateY(-5px)"
        }
      }}
    >
      {isFeatured && (
        <Chip
          label="Recommended"
          size="small"
          sx={{
            bgcolor: "secondary.main",
            color: "common.white",
            fontSize: 10,
            fontWeight: 600,
            left: "50%",
            letterSpacing: "0.08em",
            position: "absolute",
            textTransform: "uppercase",
            top: 0,
            transform: "translate(-50%, -50%)"
          }}
        />
      )}

      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          p: { xs: 3, sm: 3.5, md: 4, lg: 5 },
          "&:last-child": { pb: { xs: 3, sm: 3.5, md: 4, lg: 5 } }
        }}
      >
        <Chip
          label={plan.badge}
          size="small"
          sx={{
            alignSelf: "flex-start",
            bgcolor: isFeatured ? "rgba(255,255,255,0.14)" : alpha("#451ebb", 0.08),
            color: isFeatured ? "common.white" : "primary.main",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase"
          }}
        />

        <Typography component="h3" sx={{ fontSize: 21, fontWeight: 500, mt: 2.5 }}>
          {plan.name}
        </Typography>

        <Stack
          direction="row"
          alignItems="baseline"
          flexWrap="wrap"
          spacing={1}
          mt={2}
          useFlexGap
        >
          <Typography
            sx={{
              fontSize: { xs: 34, md: 38 },
              fontWeight: 600,
              letterSpacing: "-0.03em"
            }}
          >
            {priceLabel}
          </Typography>
          <Typography
            sx={{
              color: isFeatured ? "rgba(255,255,255,0.72)" : "text.secondary",
              fontSize: 12
            }}
          >
            {plan.duration}
          </Typography>
        </Stack>

        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: isFeatured ? "rgba(255,255,255,0.18)" : "divider",
            my: 3
          }}
        />

        <Stack component="ul" spacing={1.75} sx={{ listStyle: "none", m: 0, mb: 4, p: 0 }}>
          {plan.features.map((feature) => (
            <Stack
              component="li"
              key={feature.label}
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{ opacity: feature.included ? 1 : 0.45 }}
            >
              <CheckCircleRounded
                sx={{
                  color: isFeatured ? "#9ef1cf" : "secondary.main",
                  flexShrink: 0,
                  fontSize: 19,
                  mt: 0.15
                }}
              />
              <Typography
                sx={{
                  color: isFeatured ? "rgba(255,255,255,0.86)" : "text.secondary",
                  fontSize: 13,
                  fontWeight: 400,
                  lineHeight: 1.55,
                  textDecoration: feature.included ? "none" : "line-through"
                }}
              >
                {feature.label}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Button
          fullWidth
          variant={isFeatured ? "contained" : "outlined"}
          onClick={() => onSelect(plan)}
          endIcon={<ArrowForwardRounded />}
          sx={{
            bgcolor: isFeatured ? "common.white" : "transparent",
            borderColor: isFeatured ? "common.white" : "primary.main",
            color: "primary.main",
            fontWeight: 600,
            mt: "auto",
            py: 1.4,
            "&:hover": {
              bgcolor: isFeatured ? "#f3efff" : "primary.main",
              borderColor: "primary.main",
              color: isFeatured ? "primary.main" : "common.white"
            }
          }}
        >
          {plan.ctaLabel}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function PricingSection({
  activeAudience,
  onAudienceChange,
  onSelectPlan,
  pricing
}) {
  return (
    <Box id="pricing" component="section" sx={{ ...sectionSx, bgcolor: "landing.surfaceLow" }}>
      <PageContainer>
        <SectionHeading
          eyebrow="Tailored investment"
          title="Invest in your professional future."
          description="Whether you are hiring exceptional talent or looking for your next opportunity, choose a plan designed to help you move faster."
        />

        <PricingAudienceToggle activeAudience={activeAudience} onChange={onAudienceChange} />

        <Box
          sx={{
            alignItems: "stretch",
            display: "grid",
            gap: { xs: 3, lg: 3.5 },
            gridTemplateColumns: {
              xs: "minmax(0, 340px)",
              sm: "minmax(0, 380px)",
              md: "repeat(2, minmax(0, 380px))",
              lg: "repeat(3, minmax(0, 1fr))"
            },
            justifyContent: "center",
            maxWidth: 1140,
            mx: "auto"
          }}
        >
          {pricing.plans.map((plan, index) => {
            const isLastPlan = index === pricing.plans.length - 1;

            return (
              <Box
                key={`${activeAudience}-${plan.id}`}
                sx={{
                  gridColumn: isLastPlan ? { md: "1 / -1", lg: "auto" } : "auto",
                  height: "100%",
                  justifySelf: "center",
                  maxWidth: { xs: 340, sm: 380, lg: "none" },
                  width: "100%"
                }}
              >
                <PricingCard plan={plan} onSelect={onSelectPlan} />
              </Box>
            );
          })}
        </Box>

        <Typography
          color="text.secondary"
          sx={{ fontSize: 11, lineHeight: 1.6, mt: 4, textAlign: "center" }}
        >
          * All prices are in Indian Rupees (INR) and exclusive of taxes. Actual charges may vary
          based on applicable taxes and fees.
        </Typography>
      </PageContainer>
    </Box>
  );
}
